/**
 * Robust fetch wrapper with retry logic and timeout handling
 * Addresses issue #1531 - recurring "Failed to fetch" errors
 */

import { recordEvent, sanitizeUrlForMonitoring } from './monitoring.js';
import type { RobustFetchOptions } from './robust-fetch-types.js';
import { BASE_PATH } from '../store';
import { networkState } from './network-error-handler';
import { displayAlert } from './display-alert';

export type { RobustFetchOptions } from './robust-fetch-types.js';

/**
 * Delay helper for exponential backoff
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if an endpoint is critical (should throw errors) or non-critical (can fail silently)
 */
export function isCriticalEndpoint(url: string): boolean {
  // Authentication and core organization data are critical
  const criticalPatterns = [
    BASE_PATH + '/accounts/login',
    BASE_PATH + '/accounts/logout',
    BASE_PATH + '/organizations/\\d+$', // Main organization load
    BASE_PATH + '/accounts/list-organizations',
    // Signup/signin endpoints (issue #1531): throw the underlying network
    // error on total failure instead of returning a synthetic 503, so the
    // monitoring filter + handler try/catch can treat it as a network issue
    // rather than a server error.
    BASE_PATH + '/accounts/:create-user',
    BASE_PATH + '/accounts/[^/]+/accountStatus', // checkEmail / fetchUserStatus
    BASE_PATH + '/accounts/[^/]+/passwordRequest', // sendPassword / requestPassword
  ];

  return criticalPatterns.some((pattern) => new RegExp(pattern).test(url));
}

/**
 * Check if an error is a network error that should be retried
 */
function isRetriableError(error: any): boolean {
  if (!error) return false;

  // Network errors
  if (error.name === 'NetworkError' || (error.name === 'TypeError' && error.message === 'Failed to fetch')) {
    return true;
  }

  // Safari-specific network error
  if (error.name === 'TypeError' && error.message === 'Load failed') {
    console.log('[isRetriableError] Safari "Load failed" error detected - will retry');
    return true;
  }

  // Timeout errors
  if (error.name === 'AbortError') {
    return true;
  }

  return false;
}

/**
 * Check if an HTTP status code indicates a retriable error
 */
function isRetriableStatus(status: number): boolean {
  // Retry on server errors and specific client errors
  return (
    status === 408 || // Request Timeout
    status === 429 || // Too Many Requests
    status === 502 || // Bad Gateway
    status === 503 || // Service Unavailable
    status === 504 // Gateway Timeout
  );
}

/**
 * Robust fetch with retry logic, timeout, and better error handling
 */
export async function robustFetch(url: string, options: RobustFetchOptions = {}): Promise<Response> {
  const { retries = 3, retryDelay = 1000, timeout = 30000, silent = false, ...fetchOptions } = options;

  // OFFLINE MODE: Block mutations (POST, PUT, DELETE, PATCH) when offline
  const method = (fetchOptions?.method || 'GET').toUpperCase();
  const isMutation = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  if (!networkState.isOnline) {
    if (isMutation) {
      console.log('[robustFetch] Mutation blocked - offline:', { url, method });
      displayAlert('⚠️ Du kan ikke lagre endringer mens du er frakoblet.');
    } else {
      console.log('[robustFetch] GET skipped - offline:', url);
    }

    return new Response(
      JSON.stringify({
        error: 'Offline',
        message: isMutation ? 'Cannot perform mutations while offline' : 'Cannot fetch while offline',
      }),
      {
        status: 503,
        statusText: 'Service Unavailable (Offline)',
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  let lastError: any;
  const overallStart = performance.now();

  // Success-path per-call logging was removed deliberately: retries and final
  // failures are recorded in RUM (dabih_fetch_retry / dabih_fetch_failed), so
  // the console no longer needs a line per request.
  for (let attempt = 0; attempt < retries; attempt++) {
    const attemptStart = performance.now();
    try {
      // Add timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if we should retry based on status code
      if (isRetriableStatus(response.status) && attempt < retries - 1) {
        const backoffDelay = retryDelay * Math.pow(2, attempt);
        if (!silent) {
          console.warn(`Request failed with status ${response.status}, retrying in ${backoffDelay}ms...`, url);
        }
        await delay(backoffDelay);
        continue;
      }

      // Response obtained after one or more retries: record how much latency
      // the failed attempts + backoff added (time until the final attempt
      // started). This is the retry-rate signal from issue #13421.
      if (attempt > 0) {
        recordEvent('dabih_fetch_retry', {
          url: sanitizeUrlForMonitoring(url),
          method,
          attempts: attempt + 1,
          addedLatencyMs: Math.round(attemptStart - overallStart),
          status: response.status,
        });
      }

      return response;
    } catch (error: any) {
      console.error(`[robustFetch] Attempt ${attempt + 1} failed:`, {
        url,
        errorName: error?.name,
        errorMessage: error?.message,
        errorType: error?.constructor?.name,
        isNetworkError: error?.name === 'TypeError' && error?.message?.includes('Load failed'),
        attempt: attempt + 1,
        willRetry: attempt < retries - 1 && isRetriableError(error),
      });

      lastError = error;

      // Check if this is the last attempt
      if (attempt === retries - 1) {
        break;
      }

      // Check if error is retriable
      if (!isRetriableError(error)) {
        throw error; // Non-retriable error, fail immediately
      }

      // Calculate backoff delay
      const backoffDelay = retryDelay * Math.pow(2, attempt);

      if (!silent) {
        console.warn(
          `Network error on attempt ${attempt + 1}/${retries}, retrying in ${backoffDelay}ms...`,
          url,
          error.message,
        );
      }

      await delay(backoffDelay);
    }
  }

  // All retries exhausted
  const errorMessage = `Failed to fetch after ${retries} attempts: ${url}`;
  const finalError = new Error(errorMessage);
  (finalError as any).originalError = lastError;
  (finalError as any).url = url;

  // Report as a custom event, NOT via notifyError: the message matches the
  // ignorable network patterns (shouldReportError would always drop it), but
  // an exhausted-retry failure is exactly the signal we need in RUM to spot
  // e.g. Safari "Load failed" clusters (issues #13413/#13421). Volume is
  // bounded — this fires only after all retries are spent.
  recordEvent('dabih_fetch_failed', {
    url: sanitizeUrlForMonitoring(url),
    method,
    attempts: retries,
    errorType: lastError?.name ?? 'unknown',
    errorMessage: lastError?.message,
    online: networkState.isOnline,
    critical: isCriticalEndpoint(url),
    elapsedMs: Math.round(performance.now() - overallStart),
  });

  if (!silent && !isCriticalEndpoint(url)) {
    console.warn(errorMessage, lastError);
  }

  // For non-critical endpoints, return a synthetic error response
  if (!isCriticalEndpoint(url)) {
    return new Response(
      JSON.stringify({
        error: 'Network error',
        message: 'The request failed due to network issues',
      }),
      {
        status: 503,
        statusText: 'Service Unavailable',
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  throw finalError;
}

/**
 * Create a fetch wrapper that reports loading states
 */
export function createFetchWithLoadingState(onLoadingChange: (loading: boolean) => void): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    onLoadingChange(true);
    try {
      return await robustFetch(input.toString(), init);
    } finally {
      onLoadingChange(false);
    }
  };
}
